import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type Data = Awaited<ReturnType<typeof api.platformForwarding>>;
type Destination = Data["destinations"][number];
type RecentRun = Data["recentRuns"][number];

export default function AdminForwardingOversight() {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "paused" | "disabled">("all");

  useEffect(() => {
    let cancelled = false;
    api.platformForwarding()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const destinations = useMemo(() => (data?.destinations ?? []).filter((d) => {
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return d.workspaceName.toLowerCase().includes(q) || d.label.toLowerCase().includes(q) || d.target.toLowerCase().includes(q);
  }), [data, statusFilter, filter]);

  const tDest = useTableState(destinations as unknown as Array<Record<string, unknown>>, {
    searchFields: ["workspaceName", "label", "kind", "target", "status"],
    initialPageSize: 25
  });
  const tRuns = useTableState((data?.recentRuns ?? []) as unknown as Array<Record<string, unknown>>, {
    searchFields: ["workspaceName", "destinationId", "status", "message"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Forwarding oversight</h1>
          <p>Cross-tenant visibility into outbound forwarding destinations, schedules, and recent run health.</p>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {loading ? <SkeletonTable rows={5} columns={4} /> : data && (
        <>
          <section className="section">
            <div className="stat-grid">
              <div className="stat"><div className="stat-label">Destinations</div><div className="stat-value">{data.summary.destinationCount}</div></div>
              <div className="stat"><div className="stat-label">Active</div><div className="stat-value">{data.summary.activeDestinationCount}</div></div>
              <div className="stat"><div className="stat-label">Paused</div><div className="stat-value">{data.summary.pausedDestinationCount}</div></div>
              <div className="stat"><div className="stat-label">Schedules</div><div className="stat-value">{data.summary.scheduleCount}</div></div>
              <div className="stat">
                <div className="stat-label">Failed runs (recent)</div>
                <div className="stat-value" style={{ color: data.summary.recentFailedRuns > 0 ? "#b91c1c" : undefined }}>
                  {data.summary.recentFailedRuns}
                </div>
              </div>
            </div>
          </section>

          <section className="section">
            <div className="section-head"><h2>Destinations</h2></div>
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-body" style={{ padding: 12, display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="Filter by workspace, label, or target…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{ flex: 1 }}
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </div>
            {destinations.length === 0 ? (
              <EmptyState
                icon="📤"
                title="No forwarding destinations match"
                description="Workspace owners configure destinations from the Forwarding page. Once active, items get pushed downstream and runs appear below."
              />
            ) : (
              <div>
                <TableToolbar
                  search={tDest.search} setSearch={tDest.setSearch}
                  page={tDest.page} setPage={tDest.setPage}
                  pageSize={tDest.pageSize} setPageSize={tDest.setPageSize}
                  totalPages={tDest.totalPages} totalRows={tDest.totalRows}
                  hideExport
                  searchPlaceholder="Search destinations…"
                />
                <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th>Workspace</th><th>Label</th><th>Kind</th><th>Target</th><th>Status</th><th>Failed runs</th><th>Updated</th><th>Actions</th></tr></thead>
                  <tbody>
                    {(tDest.paged as unknown as Destination[]).map((d) => (
                      <tr key={d.id}>
                        <td>{d.workspaceName}</td>
                        <td>{d.label}</td>
                        <td className="soft">{d.kind}</td>
                        <td className="soft"><code>{d.target}</code></td>
                        <td><span className={`pill ${d.status === "active" ? "pill-ok" : d.status === "paused" ? "pill-warn" : "pill-muted"}`}>{d.status}</span></td>
                        <td className="soft" style={d.failedRunCount > 0 ? { color: "#b91c1c", fontWeight: 600 } : undefined}>{d.failedRunCount}</td>
                        <td className="soft">{new Date(d.updatedAt).toLocaleString()}</td>
                        <td>
                          <Link className="btn btn-sm" to={`/admin/workspaces/${encodeURIComponent(d.workspaceId)}`}>Open</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </section>

          <section className="section">
            <div className="section-head"><h2>Recent runs</h2></div>
            {data.recentRuns.length === 0 ? (
              <EmptyState
                icon="🕒"
                title="No forwarding runs recorded yet"
                description="Runs accumulate as destinations execute. Failed runs appear with a red status pill — investigate them from the workspace's Forwarding page."
              />
            ) : (
              <div>
                <TableToolbar
                  search={tRuns.search} setSearch={tRuns.setSearch}
                  page={tRuns.page} setPage={tRuns.setPage}
                  pageSize={tRuns.pageSize} setPageSize={tRuns.setPageSize}
                  totalPages={tRuns.totalPages} totalRows={tRuns.totalRows}
                  hideExport
                  searchPlaceholder="Search workspace / status / message…"
                />
                <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th>Started</th><th>Workspace</th><th>Destination</th><th>Status</th><th>Items</th><th>Message</th><th>Actions</th></tr></thead>
                  <tbody>
                    {(tRuns.paged as unknown as RecentRun[]).map((r) => {
                      const dest = data.destinations.find((d) => d.id === r.destinationId);
                      return (
                        <tr key={r.id}>
                          <td className="soft">{new Date(r.startedAt).toLocaleString()}</td>
                          <td>{r.workspaceName}</td>
                          <td>{dest?.label ?? r.destinationId}</td>
                          <td><span className={`pill ${r.status === "succeeded" ? "pill-ok" : r.status === "failed" ? "pill-err" : "pill-muted"}`}>{r.status}</span></td>
                          <td className="soft">{r.itemsForwarded ?? "—"}</td>
                          <td className="soft">{r.message ?? ""}</td>
                          <td>
                            <Link className="btn btn-sm" to={`/admin/workspaces/${encodeURIComponent(r.workspaceId)}`}>Open</Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
